import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import Skeleton from 'react-loading-skeleton';

import { FolderModel } from '../../../gamma-api';
import { getTranslateFunc } from '../../utils/stringUtil';
import { getFoldersPath } from '../../utils/pathUtils';
import IbIcon from '../common/IbIcon';
import IbSearch from '../IbSearch';
import IbButton from '../common/IbButton';
import IbTypography from '../common/IbTypography';
import IbPopover from '../common/IbPopover';
import IbTooltip from '../common/IbTooltip';
import IbTextHighlighter, { MIN_HIGHLIGHTED_SPAN_LENGTH } from '../common/IbTextHighlighter';
import { currentFolderIdSelector, selectedFolderIdSelector } from '../../recoil';
import { defaultApi } from '../../apis';

import {
  BUTTON_CLASS_NAME,
  CONTENT_CLASS_NAME,
  EMPTY_CLASS_NAME,
  ITEM_CLASS_NAME,
  MAIN_CLASS_NAME,
  POPOVER_CLASS_NAME,
  PREFIX_CLASS_NAME,
  SCROLL_ACTIVE_CLASS_NAME,
  SCROLL_CLASS_NAME,
  SCROLL_ITEM_COUNT,
  SKELETON_HEIGHT,
  SKELETON_WIDTH,
  TOOLTIP_MOUSE_DELAY,
} from './const';

import './index.less';

const FolderSelector: React.FC = () => {
  const translate = getTranslateFunc('Folder: Selector:');

  const { push } = useHistory();

  const currentFolderId = useRecoilValue(currentFolderIdSelector);
  const setSelectedFolderId = useSetRecoilState(selectedFolderIdSelector);

  const [folders, setFolders] = useState<FolderModel[]>([]);
  const [searchText, setSearchText] = useState<string>();
  const [popoverOpened, setPopoverOpened] = useState(false);
  const [fetching, setFetching] = useState(true);

  const fetchFoldersAsync = async () => {
    try {
      setFetching(true);
      const response = await defaultApi.getAllFolders();
      setFolders(response.data);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
      setFolders([]);
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => {
    if (popoverOpened) {
      void fetchFoldersAsync();
    }
  }, [popoverOpened]);

  useEffect(() => void fetchFoldersAsync(), []);

  const onSearchChange = (value?: string) => {
    setSearchText(value);
  };

  const onPopoverToggle = (value?: boolean) => {
    setPopoverOpened(value ?? false);
  };

  const onFolderClick = (folder: FolderModel) => () => {
    setSelectedFolderId(folder.folder_id);
    setPopoverOpened(false);
  };

  const onButtonClick = () => {
    push(getFoldersPath());
    setPopoverOpened(false);
  };

  const filterFolders = (): FolderModel[] => {
    const term = searchText?.trim().toLowerCase() || '';
    return term.length < MIN_HIGHLIGHTED_SPAN_LENGTH
      ? folders
      : folders.filter((folder) => folder.name.toLowerCase().indexOf(term) >= 0);
  };

  const getTitle = (): string => {
    if (!folders.length) {
      return '-';
    }

    if (!currentFolderId) {
      return folders[0].name;
    }

    for (const folder of folders) {
      if (folder.folder_id === currentFolderId) {
        return folder.name;
      }
    }

    return '-';
  };

  const renderTitle = () => {
    if (fetching && !folders.length) {
      return <Skeleton height={SKELETON_HEIGHT} width={SKELETON_WIDTH} />;
    }

    const title = getTitle();
    return (
      <div className={PREFIX_CLASS_NAME}>
        <IbTooltip mouseEnterDelay={TOOLTIP_MOUSE_DELAY} placement="left" title={title}>
          <IbTypography>
            <IbTypography.Paragraph type="secondary">{title}</IbTypography.Paragraph>
          </IbTypography>
        </IbTooltip>
      </div>
    );
  };

  const renderArrow = () => {
    const iconName = popoverOpened ? 'up' : 'down';
    return <IbIcon iconName={iconName} size={20} />;
  };

  const renderFolder = (folder: FolderModel) => {
    return (
      <div key={folder.folder_id} className={ITEM_CLASS_NAME} onClick={onFolderClick(folder)}>
        <IbTooltip mouseEnterDelay={TOOLTIP_MOUSE_DELAY} placement="left" title={folder.name}>
          <IbTypography>
            <IbTypography.Paragraph type="secondary">
              <IbTextHighlighter highlightedText={searchText} text={folder.name} />
            </IbTypography.Paragraph>
          </IbTypography>
          {folder.folder_id === currentFolderId && <IbIcon iconName="check-one" size={20} />}
        </IbTooltip>
      </div>
    );
  };

  const renderScroll = () => {
    const filteredFolders = filterFolders();
    if (!filteredFolders.length) {
      return <div className={EMPTY_CLASS_NAME}>{translate('Nothing found')}</div>;
    }

    const classes = [SCROLL_CLASS_NAME];
    if (filteredFolders.length > SCROLL_ITEM_COUNT) {
      classes.push(SCROLL_ACTIVE_CLASS_NAME);
    }
    return <div className={classes.join(' ')}>{filteredFolders.map(renderFolder)}</div>;
  };

  const renderContent = () => {
    return (
      <div className={CONTENT_CLASS_NAME}>
        <IbSearch placeholder={translate('Search: Placeholder')} searchValue={searchText} onChange={onSearchChange} />
        {renderScroll()}
        <IbButton type="fill" onClick={onButtonClick}>
          {translate('Button: Title')}
        </IbButton>
      </div>
    );
  };

  const renderButton = () => {
    return (
      <button className={BUTTON_CLASS_NAME}>
        {renderTitle()}
        {renderArrow()}
      </button>
    );
  };

  const renderPopover = () => {
    return (
      <IbPopover
        className={POPOVER_CLASS_NAME}
        content={renderContent()}
        placement="bottomLeft"
        trigger="click"
        visible={popoverOpened}
        onVisibleChange={onPopoverToggle}
      >
        {renderButton()}
      </IbPopover>
    );
  };

  return <div className={MAIN_CLASS_NAME}>{renderPopover()}</div>;
};

export default FolderSelector;
